/**
 * Setup Complete page — ported from dashboard/app/setup-complete/page.tsx.
 * Uses TanStack Router APIs instead of next/navigation.
 * Handles post-payment account setup (password or Google).
 */
import { useState, useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { motion, AnimatePresence } from 'framer-motion'
import { Eye, EyeOff, Loader2, AlertCircle, Lock, ArrowRight } from 'lucide-react'
import {
  validateSetupToken,
  completeSetupWithPassword,
  initiateGoogleSetup,
  getCurrentUser,
  type SetupUserInfo,
} from '../lib/auth-client'
import { slideInLeft, slideInRight } from '../components/animations'
import { aliasAndIdentifyFrom } from '../lib/mixpanel'

export const Route = createFileRoute('/setup-complete')({
  validateSearch: (search: Record<string, unknown>): { token?: string; error?: string; mpid?: string } => ({
    token: (search.token as string) || undefined,
    error: (search.error as string) || undefined,
    mpid: (search.mpid as string) || undefined,
  }),
  head: () => ({
    meta: [{ title: 'Setup Complete | Unbreakable' }],
  }),
  component: SetupCompletePage,
})

function SetupCompletePage() {
  const navigate = useNavigate()
  const { token, error: errorParam, mpid } = Route.useSearch()

  // States
  const [loading, setLoading] = useState(true)
  const [userInfo, setUserInfo] = useState<SetupUserInfo | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [setupComplete, setSetupComplete] = useState(false)
  const requiresName = !userInfo?.name

  // Validate token on mount
  useEffect(() => {
    async function validateToken() {
      if (!token) {
        setError('No setup token provided')
        setLoading(false)
        return
      }

      if (errorParam) {
        const errorMessages: Record<string, string> = {
          session_expired: 'Your session has expired. Please try again.',
          invalid_session: 'Invalid session. Please try again.',
          user_not_found: 'User not found.',
          already_complete: 'Your account setup has already been completed. Please log in.',
        }
        setError(errorMessages[errorParam] || 'An error occurred. Please try again.')
        setLoading(false)
        return
      }

      try {
        const info = await validateSetupToken(token)
        if (info) {
          setUserInfo(info)
        } else {
          setError('Invalid or expired setup link. Please contact support.')
        }
      } catch {
        setError('Failed to validate setup link.')
      } finally {
        setLoading(false)
      }
    }

    validateToken()
  }, [token, errorParam])

  useEffect(() => {
    if (userInfo?.name) {
      setFullName(userInfo.name)
    }
  }, [userInfo?.name])

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (requiresName && !fullName.trim()) {
      setError('Please enter your name to continue')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setSubmitting(true)

    try {
      await completeSetupWithPassword(token!, password, requiresName ? fullName.trim() : undefined)

      // Alias and identify user after successful setup
      const user = await getCurrentUser()
      if (user) {
        aliasAndIdentifyFrom(
          user.id,
          mpid,
          {
            $email: user.email,
            ...(user.name ? { $name: user.name } : {}),
          }
        )
      }

      setSetupComplete(true)
      // Redirect to dashboard after brief celebration
      setTimeout(() => {
        navigate({ to: '/' })
      }, 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete setup')
    } finally {
      setSubmitting(false)
    }
  }

  const handleGoogleClick = async () => {
    setGoogleLoading(true)
    setError(null)

    try {
      if (requiresName && !fullName.trim()) {
        setError('Please enter your name to continue')
        setGoogleLoading(false)
        return
      }
      const { redirectUrl } = await initiateGoogleSetup(
        token!,
        requiresName ? fullName.trim() : undefined,
        mpid
      )
      window.location.href = redirectUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate Google sign-in')
      setGoogleLoading(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-linear-to-br from-brand/5 via-white to-emerald-50/30 flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Loader2 className="w-8 h-8 animate-spin text-brand mx-auto mb-4" />
          <p className="text-gray-600">Verifying your setup link...</p>
        </motion.div>
      </div>
    )
  }

  // Error state (invalid token)
  if (error && !userInfo) {
    return (
      <div className="min-h-screen bg-linear-to-br from-brand/5 via-white to-emerald-50/30 flex items-center justify-center p-4">
        <motion.div
          className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Setup Link Invalid</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate({ to: '/login' })}
            className="px-6 py-3 bg-brand text-white rounded-xl hover:bg-brand-hover transition-colors"
          >
            Go to Login
          </button>
        </motion.div>
      </div>
    )
  }

  // Setup complete celebration
  if (setupComplete) {
    return (
      <div className="min-h-screen bg-linear-to-br from-brand/5 via-white to-emerald-50/30 flex items-center justify-center p-4">
        <motion.div
          className="w-full max-w-md text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.h1
            className="text-3xl font-bold text-gray-900 mb-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            You&apos;re All Set!
          </motion.h1>

          <motion.p
            className="text-gray-600 mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Your account is ready. Taking you to your dashboard...
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Loader2 className="w-6 h-6 animate-spin text-brand mx-auto" />
          </motion.div>
        </motion.div>
      </div>
    )
  }

  // Main setup form
  return (
    <div className="min-h-screen bg-linear-to-br from-brand/5 via-white to-emerald-50/30 flex items-center justify-center p-4">
      <motion.div
        className="w-full max-w-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Success Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          <p className="text-lg text-gray-600">
            Welcome{userInfo?.name ? `, ${userInfo.name}` : ''}! One more step to go.
          </p>
        </div>

        {/* Setup Card */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <motion.h2
            className="text-xl font-semibold text-gray-900 text-center mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Set up your login
          </motion.h2>

          {/* Error display */}
          <AnimatePresence>
            {error && (
              <motion.div
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {requiresName && (
            <motion.div
              className="mb-6"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
            >
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1.5">
                Your Name
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                disabled={submitting || googleLoading}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/50 outline-none transition-all disabled:bg-gray-50 disabled:text-gray-500"
              />
            </motion.div>
          )}

          {/* Google Sign-In Option */}
          <motion.div
            variants={slideInLeft}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.6 }}
          >
            <button
              type="button"
              onClick={handleGoogleClick}
              disabled={googleLoading || submitting}
              className="w-full flex items-center justify-center gap-3 px-4 py-3.5 border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all group"
            >
              {googleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              <span className="font-medium text-gray-700 group-hover:text-gray-900">
                {googleLoading ? 'Redirecting...' : 'Continue with Google'}
              </span>
              <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                Fastest
              </span>
            </button>
          </motion.div>

          {/* Divider */}
          <motion.div
            className="my-6 relative"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-white text-sm text-gray-500">or</span>
            </div>
          </motion.div>

          {/* Password Form */}
          <motion.form
            onSubmit={handlePasswordSubmit}
            variants={slideInRight}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.8 }}
          >
            <div className="space-y-4">
              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Create Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    disabled={submitting || googleLoading}
                    className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-200 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/50 outline-none transition-all disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    disabled={submitting || googleLoading}
                    className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-200 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/50 outline-none transition-all disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={submitting || googleLoading || !password || !confirmPassword}
                className="w-full py-3.5 bg-brand text-white font-medium rounded-xl hover:bg-brand-hover active:bg-brand-active disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    Complete Setup
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </div>
          </motion.form>
        </motion.div>

        {/* Footer */}
        <motion.p
          className="text-center text-sm text-gray-500 mt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          Having trouble?{' '}
          <a href="mailto:support@unbreakableapp.com" className="text-brand hover:underline">
            Contact support
          </a>
        </motion.p>
      </motion.div>
    </div>
  )
}
